import { schemas } from '#oaf_schema.registry';
import { createStore, selectUrl } from '@oneappexchange/lwc-wired';
import { IS_DEVELOPMENT } from '~/config';
import { scrollToTop } from '~/modules/utilities/browserUtilities';
import { throwDevelopmentOrLog } from '~/modules/utilities/errorUtilities';
import { upsertPublisher } from '../../services/publisherService';
import { notifyFailure, notifySuccess } from '../../utilities/notificationUtilities';
import { i18nStore } from '../i18nStore';
import { PUBLISHER_ROUTE, routerStore } from '../routerStore';
import { japanesePublisherEditorStore, selectJpPublisher } from './japanesePublisherEditorStore';
import { publisherStore, selectLogoSetItems, selectPublisher } from './publisherStore';
const initialState = {
  isSaving: false,
  hasChanged: false,
  uploadingAssets: 0,
  publisher: null
};
export const publisherEditorStore = createStore(initialState, (set, get) => ({
  reset() {
    set(function reset() {
      return initialState;
    });
  },
  change() {
    set(function change(draft) {
      draft.hasChanged = true;
    });
  },
  resetChanged() {
    set(function resetChanged(draft) {
      draft.hasChanged = false;
    });
  },
  setPublisher(publisher) {
    set(function setPublisher(draft) {
      draft.publisher = publisher;
    });
  },
  setSmallLogoUrl(mediaId) {
    set(function setSmallLogoUrl(draft) {
      const items = selectLogoSetItems(draft, true);
      const logo = items.find(test => test.logoType === 'Logo');
      if (!logo) {
        items.push(schemas['publisher/plugins/PublisherLogo/PublisherLogoItem'].new({
          logoType: 'Logo',
          mediaId
        }));
      } else {
        logo.mediaId = mediaId;
      }
    });
  },
  incrementUploadingAssets() {
    set(function incrementUploadingAssets(draft) {
      draft.uploadingAssets++;
    });
  },
  decrementUploadingAssets() {
    set(function decrementUploadingAssets(draft) {
      draft.uploadingAssets--;
      if (draft.uploadingAssets < 0) {
        draft.uploadingAssets = 0;
      }
    });
  },
  setName(name) {
    set(function setName(draft) {
      draft.publisher.name = name;
    });
  },
  setWebsite(website) {
    set(function setWebsite(draft) {
      draft.publisher.website = website;
    });
  },
  setEmail(email) {
    set(function setEmail(draft) {
      draft.publisher.email = email;
    });
  },
  setBusinessEmail(email) {
    set(function setBusinessEmail(draft) {
      draft.publisher.businessEmail = email;
    });
  },
  setMarketingEmail(email) {
    set(function setMarketingEmail(draft) {
      draft.publisher.marketingEmail = email;
    });
  },
  setTechnicalEmail(email) {
    set(function setTechnicalEmail(draft) {
      draft.publisher.technicalEmail = email;
    });
  },
  setSecurityReviewEmail(email) {
    set(function setSecurityReviewEmail(draft) {
      draft.publisher.securityReviewEmail = email;
    });
  },
  setHQLocation(hQLocation) {
    set(function setHQLocation(draft) {
      draft.publisher.hQLocation = hQLocation;
    });
  },
  setYearFounded(yearFounded) {
    set(function setYearFounded(draft) {
      draft.publisher.yearFounded = yearFounded;
    });
  },
  setEmployees(employees) {
    set(function setEmployees(draft) {
      draft.publisher.employees = employees;
    });
  },
  setDescription(description) {
    set(function setDescription(draft) {
      draft.publisher.description = description;
    });
  },
  setPhoneNumber(phone) {
    set(function setPhoneNumber(draft) {
      draft.publisher.phoneNumber = phone;
    });
  },
  setJapaneseMarketplaceTranslation(publisher) {
    set(function setJapaneseMarketplaceTranslation(draft) {
      if (!draft.publisher.marketplaceLocalization) {
        draft.publisher.marketplaceLocalization = {};
      }
      draft.publisher.marketplaceLocalization.jp = publisher;
    });
  },
  async savePublisher(showSuccessNotification = true, options = {}) {
    const defaultOptions = {
      successLabel: i18nStore.getState().i18nStaticCache.notifications.publisherSaved,
      failedLabel: i18nStore.getState().i18nStaticCache.notifications.publisherSaveFailed
    };
    const config = {
      ...defaultOptions,
      ...options
    };
    let success = false;
    const {
      publisher
    } = get();
    if (!publisher) {
      throwDevelopmentOrLog(new Error(`Called 'savePublisher' with a null publisher.`));
      return success;
    }
    set(function isSaving(draft) {
      draft.isSaving = true;
    });
    success = await upsertPublisher(publisher);
    if (success) {
      // Refresh publisher in order to get all of its generated external_ids
      publisherEditorStore.setPublisher(await publisherStore.fetchPublisher());
      if (showSuccessNotification) {
        notifySuccess(config.successLabel);
      }
    } else {
      notifyFailure(config.failedLabel);
    }
    set(function notSaving(draft) {
      draft.isSaving = false;
      draft.hasChanged = false;
    });
    return success;
  }
}), 'PUBLISHER_EDITOR', IS_DEVELOPMENT);

// Initialize the store only when we have a publisher and we're in the right page
function checkShouldInitialize() {
  var _publisherEditorStore;
  const {
    publisher
  } = publisherStore.getState();
  const {
    url
  } = routerStore.getState();
  if (!publisher || !url || !PUBLISHER_ROUTE.includes(url)) {
    publisherEditorStore.reset();
    return;
  }
  if (((_publisherEditorStore = publisherEditorStore.getState().publisher) === null || _publisherEditorStore === void 0 ? void 0 : _publisherEditorStore.external_id) !== publisher.external_id) {
    scrollToTop();
  }
  publisherEditorStore.setPublisher(publisher);
  publisherEditorStore.resetChanged();
}
routerStore.subscribe(checkShouldInitialize, selectUrl);
publisherStore.subscribe(checkShouldInitialize, selectPublisher);

// This module does not load until the publisher editor page is loaded, so check if we should initialize this store as soon as the module loads
checkShouldInitialize();

// Track changes to our own store, to prevent users to leave the page if they make a change without saving
function checkShouldChange() {
  const {
    isLoading,
    publisher
  } = publisherStore.getState();
  const {
    isSaving
  } = publisherEditorStore.getState();
  if (!publisher || isLoading || isSaving) {
    return;
  }
  if (!publisherEditorStore.getState().hasChanged) {
    publisherEditorStore.change();
  }
}
publisherEditorStore.subscribe(checkShouldChange, selectPublisher);

// Track changes in japanesePublisherEditorStore and update our own internal state (we are the source of truth, what gets saved into the DB)
function updateJapanesePublisher(publisher) {
  // Due to an order of operations issue, it's possible this callback is fired before the publisherEditorStore is fully initialized,
  // this check protects against null error assignment while we're initializing the publisherEditorStore.
  if (!selectPublisher(publisherEditorStore.getState())) {
    return;
  }
  publisherEditorStore.setJapaneseMarketplaceTranslation(publisher);
}
japanesePublisherEditorStore.subscribe(updateJapanesePublisher, selectJpPublisher);
export function selectIsSaving(state) {
  return state.isSaving;
}